import { Component, signal, computed, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { Title, Meta } from '@angular/platform-browser';
import { MainLayout } from '../../layouts/main-layout';
import { Resolvers } from '../../types/Resolvers';
import { getDataFromResolver } from '../../services/resolvers';
import { MarcaVehiculo } from '../../types/vehicle.types';
import { SmartCapitalizePipe } from '../../utils/pipes/smart-capitalize.pipe';
import { MarginWrapper } from '../../layouts/margin-wrapper';
import { toSlug } from '../../utils/slugs/slug.utils';

interface VehicleBrand {
  name: string;
  letter: string;
}

@Component({
  selector: 'allas-comprar-por-marca',
  imports: [
    MainLayout,
    FormsModule,
    CommonModule,
    SmartCapitalizePipe,
    MarginWrapper,
  ],
  template: `
    <allas-main-layout [showPopularSections]="false" [showNewsletter]="false">
      <allas-margin-wrapper styleClass="fs-condensed">
        <ng-container [ngTemplateOutlet]="heroSection"></ng-container>
        <ng-container [ngTemplateOutlet]="brandsList"></ng-container>
        <ng-container [ngTemplateOutlet]="contactSection"></ng-container>
      </allas-margin-wrapper>
    </allas-main-layout>

    <ng-template #heroSection>
      <section
        class="bg-white border-b"
        role="banner"
        aria-labelledby="hero-title"
      >
        <div class="container mx-auto px-4 py-8 lg:py-12">
          <div class="max-w-4xl">
            <h1
              id="hero-title"
              class="text-3xl lg:text-4xl font-bold text-gray-900 mb-4 font-inter"
            >
              COMPRAR POR MARCA
            </h1>
            <p class="text-lg text-gray-600 mb-8 font-inter">
              ALLAS REPUESTOS tiene una amplia selección de repuestos para cada
              marca de vehículo en esta lista.
            </p>
            <ng-container [ngTemplateOutlet]="searchBar"></ng-container>
          </div>
        </div>
      </section>
    </ng-template>

    <ng-template #searchBar>
      <div class="relative max-w-md" role="search">
        <label for="brand-search" class="sr-only"
          >Buscar marca de vehículo</label
        >
        <input
          id="brand-search"
          type="text"
          [ngModel]="searchQuery()"
          (ngModelChange)="searchQuery.set($event)"
          placeholder="Buscar marca de vehículo..."
          [attr.aria-label]="'Buscar marca de vehículo'"
          autocomplete="off"
          class="w-full px-4 py-3 pr-12 bg-gray-100 border-0 rounded-xl focus:bg-white focus:ring-2 focus:ring-red-500 outline-none transition-all duration-200 placeholder:text-gray-500"
        />
        <svg
          class="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          width="20"
          height="20"
          aria-hidden="true"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          ></path>
        </svg>
      </div>
    </ng-template>

    <ng-template #brandsList>
      <main
        class="container mx-auto px-4 py-8 lg:py-12"
        role="main"
        aria-labelledby="brands-title"
      >
        <h2 id="brands-title" class="sr-only">Lista de marcas de vehículos</h2>
        @if (isLoading()) {
        <ng-container [ngTemplateOutlet]="loadingState"></ng-container>
        } @else if (searchQuery() && filteredBrands().length === 0) {
        <ng-container [ngTemplateOutlet]="noResultsState"></ng-container>
        } @else {
        <ng-container [ngTemplateOutlet]="brandsGrid"></ng-container>
        }
      </main>
    </ng-template>

    <ng-template #loadingState>
      <div class="text-center py-12" role="status" aria-live="polite">
        <div class="text-gray-400 mb-4">
          <svg
            class="mx-auto w-8 h-8 animate-spin"
            fill="none"
            viewBox="0 0 24 24"
            width="32"
            height="32"
            aria-hidden="true"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            ></circle>
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
        </div>
        <h3 class="text-xl font-semibold text-gray-900 mb-2 font-inter">
          Cargando marcas de vehículos...
        </h3>
        <p class="text-gray-600 font-inter">
          Estamos cargando la información más actualizada de vehículos.
        </p>
      </div>
    </ng-template>

    <ng-template #noResultsState>
      <div class="text-center py-12" role="status" aria-live="polite">
        <div class="text-gray-400 mb-4">
          <svg
            class="mx-auto w-12 h-12"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            width="48"
            height="48"
            aria-hidden="true"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            ></path>
          </svg>
        </div>
        <h3 class="text-xl font-semibold text-gray-900 mb-2 font-inter">
          No se encontraron marcas
        </h3>
        <p class="text-gray-600 font-inter">
          No hay marcas que coincidan con "{{ searchQuery() }}". Intenta con
          otro término de búsqueda.
        </p>
      </div>
    </ng-template>

    <ng-template #brandsGrid>
      <div
        class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6"
        aria-label="Marcas de vehículos organizadas alfabéticamente"
      >
        @for (letter of availableLetters(); track letter) { @if
        (brandsByLetter()[letter]) {
        <ng-container
          [ngTemplateOutlet]="letterSection"
          [ngTemplateOutletContext]="{ $implicit: letter }"
        ></ng-container>
        } }
      </div>
    </ng-template>

    <ng-template #letterSection let-letter>
      <section class="brand-letter-section">
        <h3
          class="text-2xl font-bold text-gray-900 mb-3 font-inter border-b-2 border-red-500 pb-1"
          [id]="'letter-' + letter"
        >
          {{ letter }}
        </h3>
        <ul class="space-y-1" [attr.aria-labelledby]="'letter-' + letter">
          @for (brand of getBrandsToShow(letter); track brand.name) {
          <ng-container
            [ngTemplateOutlet]="brandItem"
            [ngTemplateOutletContext]="{ $implicit: brand }"
          ></ng-container>
          }
        </ul>
        @if (hasMoreBrands(letter)) {
        <button
          (click)="toggleShowAll(letter)"
          class="mt-2 text-xs text-red-600 hover:text-red-700 font-medium"
          [attr.aria-expanded]="showAllBrands()[letter] ? 'true' : 'false'"
          [attr.aria-controls]="'brands-' + letter"
          [title]="
            showAllBrands()[letter]
              ? 'Ocultar marcas adicionales'
              : 'Mostrar más marcas'
          "
        >
          {{
            showAllBrands()[letter]
              ? 'Ver menos'
              : 'Ver más (' + (brandsByLetter()[letter].length - 8) + ')'
          }}
        </button>
        }
      </section>
    </ng-template>

    <ng-template #brandItem let-brand>
      <li>
        <a
          [href]="getBrandUrl(brand.name)"
          class="group flex items-center justify-between text-gray-700 hover:text-red-600 transition-colors duration-200 py-0.5"
          [attr.aria-label]="'Ver repuestos para ' + brand.name"
          [title]="'Ver repuestos para ' + brand.name"
        >
          <span class="font-inter text-sm">
            Repuestos para {{ brand.name | smartCapitalize }}
          </span>
          <span
            class="opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-red-500"
            aria-hidden="true"
          >
            →
          </span>
        </a>
      </li>
    </ng-template>

    <ng-template #contactSection>
      <section class="bg-gray-100 border-t" aria-labelledby="contact-title">
        <div class="container mx-auto px-4 py-8">
          <div class="text-center">
            <h3
              id="contact-title"
              class="text-lg font-semibold text-gray-900 mb-2 font-inter"
            >
              ¿No encuentras la marca que buscas?
            </h3>
            <p class="text-gray-600 mb-4 font-inter">
              Contáctanos y te ayudamos a encontrar los repuestos que necesitas
            </p>
            <div class="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contacto"
                class="inline-flex items-center justify-center px-6 py-3 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors duration-200"
                [attr.aria-label]="'Ir a la página de contacto'"
                title="Ir a la página de contacto"
              >
                Contactar Ahora
              </a>
              <a
                href="https://wa.me/50495089953"
                target="_blank"
                rel="noopener noreferrer"
                class="inline-flex items-center justify-center px-6 py-3 bg-[#0A5727] text-white font-semibold rounded-lg hover:bg-[#0A3D1D] transition-colors duration-200"
                [attr.aria-label]="
                  'Contactar por WhatsApp al número +504 9508-9953'
                "
                title="Contactar por WhatsApp"
              >
                WhatsApp: +504 9508-9953
              </a>
            </div>
          </div>
        </div>
      </section>
    </ng-template>
  `,
})
export class ComprarPorMarca implements OnInit {
  private route = inject(ActivatedRoute);
  private title = inject(Title);
  private meta = inject(Meta);

  searchQuery = signal('');
  showAllBrands = signal<Record<string, boolean>>({});
  isLoading = signal(true);

  // Convert ALL_VEHICULOS to VehicleBrand format
  private vehicleBrands: VehicleBrand[] = [];

  constructor() {
    this.getVehicleBrands();
  }

  ngOnInit() {
    this.setMetaTags();
  }

  private setMetaTags() {
    const pageTitle = 'Comprar por Marca de Vehículo | Allas Repuestos';
    const description =
      'Encuentra repuestos para tu marca de vehículo. Toyota, Honda, Ford, Chevrolet y más. Amplia selección de repuestos con envío a domicilio en Honduras.';

    this.title.setTitle(pageTitle);

    this.meta.updateTag({
      name: 'description',
      content: description,
    });

    this.meta.updateTag({
      property: 'og:title',
      content: pageTitle,
    });

    this.meta.updateTag({
      property: 'og:description',
      content: description,
    });

    this.meta.updateTag({
      property: 'og:type',
      content: 'website',
    });

    this.meta.updateTag({
      name: 'twitter:card',
      content: 'summary_large_image',
    });

    this.meta.updateTag({
      name: 'twitter:title',
      content: pageTitle,
    });

    this.meta.updateTag({
      name: 'twitter:description',
      content: description,
    });
  }

  async getVehicleBrands(): Promise<void> {
    try {
      // Intentar obtener datos del resolver primero
      const data = getDataFromResolver<MarcaVehiculo[]>(
        Resolvers.ALL_VEHICLE_BRANDS,
        this.route
      );

      if (!data) {
        console.error('No se encontraron datos de marcas de vehículos');
        return;
      }

      this.vehicleBrands = data.map((brand) => ({
        name: brand.marca,
        letter: brand.marca.charAt(0).toUpperCase(),
      }));
    } catch (error) {
      console.error('Error inicializando vehículos en ComprarPorMarca:', error);
    } finally {
      this.isLoading.set(false);
    }
  }

  // Filter brands based on search query
  filteredBrands = computed(() => {
    if (!this.searchQuery()) return this.vehicleBrands;
    return this.vehicleBrands.filter((brand) =>
      brand.name.toLowerCase().includes(this.searchQuery().toLowerCase())
    );
  });

  // Group brands by letter
  brandsByLetter = computed(() => {
    const grouped: Record<string, VehicleBrand[]> = {};
    this.filteredBrands().forEach((brand) => {
      if (!grouped[brand.letter]) {
        grouped[brand.letter] = [];
      }
      grouped[brand.letter].push(brand);
    });
    return grouped;
  });

  // Get all letters that have brands
  availableLetters = computed(() => {
    return Object.keys(this.brandsByLetter()).sort();
  });

  toggleShowAll(letter: string) {
    this.showAllBrands.update((prevState) => ({
      ...prevState,
      [letter]: !prevState[letter],
    }));
  }

  getBrandsToShow(letter: string): VehicleBrand[] {
    const showAll = this.showAllBrands()[letter] || false;
    const brands = this.brandsByLetter()[letter];
    return showAll ? brands : brands.slice(0, 8);
  }

  hasMoreBrands(letter: string): boolean {
    return this.brandsByLetter()[letter].length > 8;
  }

  getBrandUrl(brandName: string): string {
    // Marca con vehículos populares - usar ruta específica
    return '/repuestos/v/' + toSlug(brandName);
  }
}
